import { Injectable, inject } from '@angular/core';
import { Observable, filter, first, map, merge, withLatestFrom } from 'rxjs';
import { ProjectStore } from '../../state/project/project.store';
import { HeatPumpIntermediaryStore } from '../../state/heat-pump-intermediary/heat-pump-intermediary.store';
import { HeatPumpIntermediaryFormService } from './heat-pump-intermediary-form.service';
import { HeatPumpIntermediaryFormOptionsService } from './heat-pump-intermediary-form-options.service';
import { ProjectsService } from '../../services';
import { FormName, HeatPumpIntermediarySuccessMessages, ProjectType } from '@nx-customer-apps/shared/enums';
import { BuildingType, HeatLoadCalculationMethod, LodashService } from '@nx-customer-apps/components';
import { ProjectResponseGetById } from '@nx-customer-apps/api-planning-projects';
import { HeatPumpIntermediaryState, SubsidyEligibility } from '@nx-customer-apps/shared/interfaces';
import { TranslateService } from '@ngx-translate/core';
import { HeatPumpIntermediaryPrefillService } from './heat-pump-intermediary-prefill.service';
import { Question } from '@nx-customer-apps/shared/utils';
import { HeatPumpIntermediaryService } from './heat-pump-intermediary.service';
import { HeatPumpIntermediaryPrefillMapper } from './heat-pump-intermediary-prefill.mapper';
import { AccordionService } from '../../shared/interfaces';

@Injectable({
    providedIn: 'root'
})
export class HeatPumpIntermediarySubsidyEligibilityService implements AccordionService {
    private projectStore = inject(ProjectStore);
    private heatPumpIntermediaryStore = inject(HeatPumpIntermediaryStore);
    private heatPumpIntermediaryService = inject(HeatPumpIntermediaryService);
    private formService = inject(HeatPumpIntermediaryFormService);
    private formOptionsService = inject(HeatPumpIntermediaryFormOptionsService);
    private translate = inject(TranslateService);
    private projectsService = inject(ProjectsService);
    private prefillService = inject(HeatPumpIntermediaryPrefillService);
    private prefillMapper = inject(HeatPumpIntermediaryPrefillMapper);

    public questions$ = merge(
        this.qustionsForRenovationProjectCalculatedByConsumption$(),
        this.qustionsForRenovationProjectCalculatedByOtherMethods$()
    );

    public getSuccessMessage(subsidyEligibility: SubsidyEligibility, buildingType: BuildingType): string {
        if (buildingType === BuildingType.MultiFamilyHouse) {
            return subsidyEligibility.numberOfResidentialUnits ? `${subsidyEligibility.numberOfResidentialUnits}` : '';
        }

        const message = (value: boolean) => {
            const text = value ? HeatPumpIntermediarySuccessMessages.Yes : HeatPumpIntermediarySuccessMessages.No;
            return this.translate.instant(text);
        };

        const messages = [
            subsidyEligibility?.isIncomeLowForSubsidy,
            subsidyEligibility?.isOwnerOfTheBuilding,
            subsidyEligibility?.isOwnerMainResidence,
            subsidyEligibility?.isOldHeatingSystemDefective
        ]
            .filter(subsidy => !LodashService.isNil(subsidy))
            .map(subsidy => message(subsidy!));

        return messages.join(', ');
    }

    public isPrefillNeeded(): boolean {
        return !this.prefillService.isPrefilled(FormName.SubsidyEligibility);
    }

    public afterPrefill(): void {
        this.prefillService.setAsPrefilled(FormName.SubsidyEligibility);
    }

    private qustionsForRenovationProjectCalculatedByConsumption$(): Observable<Question[] | null> {
        return this.heatPumpIntermediaryStore.livingArea$.pipe(
            filter(Boolean),
            withLatestFrom(
                this.projectStore.project$,
                this.heatPumpIntermediaryStore.isOldDeviceUsed$,
                this.heatPumpIntermediaryStore.cache$,
                (livingArea, project, isOldDeviceUsed, cache) => ({
                    livingArea,
                    project,
                    isOldDeviceUsed,
                    cache
                })
            ),
            filter(
                ({ project }) =>
                    this.projectsService.isRenovation(project!.projectType as ProjectType) &&
                    this.projectsService.isCalculatedBy(project!, HeatLoadCalculationMethod.Consumption) &&
                    this.heatPumpIntermediaryService.isSubsidyPossible(project!)
            ),
            map(({ livingArea, project, isOldDeviceUsed, cache }) => {
                if (!livingArea) {
                    return null;
                }

                return this.returnQuestions(project!, { cache, isOldDeviceUsed });
            })
        );
    }

    private qustionsForRenovationProjectCalculatedByOtherMethods$(): Observable<Question[] | null> {
        return this.heatPumpIntermediaryStore.heatingDistribution$.pipe(
            withLatestFrom(
                this.projectStore.project$,
                this.heatPumpIntermediaryStore.isOldDeviceUsed$,
                this.heatPumpIntermediaryStore.cache$,
                (heatingDistribution, project, isOldDeviceUsed, cache) => ({
                    heatingDistribution,
                    project,
                    isOldDeviceUsed,
                    cache
                })
            ),
            filter(
                ({ project }) =>
                    this.projectsService.isRenovation(project!.projectType as ProjectType) &&
                    !this.projectsService.isCalculatedBy(project!, HeatLoadCalculationMethod.Consumption) &&
                    this.heatPumpIntermediaryService.isSubsidyPossible(project!)
            ),
            map(({ heatingDistribution, project, isOldDeviceUsed, cache }) => {
                if (!heatingDistribution) {
                    return null;
                }

                return this.returnQuestions(project!, { cache, isOldDeviceUsed });
            })
        );
    }

    private returnQuestions(
        project: ProjectResponseGetById,
        options: { cache?: HeatPumpIntermediaryState; isOldDeviceUsed?: { value: boolean } } = {}
    ) {
        const { cache, isOldDeviceUsed } = options;
        const cachePrefill = this.prefillMapper.stateToSubsidyPrefill(cache?.subsidyEligibility);
        const projectPrefill = this.prefillMapper.projectToSubsidyPrefill(project);
        const formOptions = this.formOptionsService.getSubsidyEligibilityFormOptions({
            prefill: cachePrefill || projectPrefill,
            projectType: project?.projectType as ProjectType,
            isOldDeviceUsed: isOldDeviceUsed?.value,
            buildingType: project?.building?.buildingType as BuildingType
        });
        return this.formService.getSubsidyEligibilityQuestions(formOptions);
    }

    public isExpanded$ = merge(this.isSubmitedPreviousFormSameValue$(), this.isPrefillCompleted$());

    public isSubmitedPreviousFormSameValue$(): Observable<boolean> {
        return this.heatPumpIntermediaryStore.sameValueSubmited$.pipe(
            withLatestFrom(this.projectStore.project$, (form, project) => ({ form, project })),
            map(({ form, project }) => {
                const afterLivingAreaForm =
                    form?.formName === FormName.LivingArea &&
                    this.projectsService.isRenovation(project!.projectType as ProjectType) &&
                    this.projectsService.isCalculatedBy(project!, HeatLoadCalculationMethod.Consumption);

                const afterHeatingDistributionForm = form?.formName === FormName.HeatingDistribution;

                return afterLivingAreaForm || afterHeatingDistributionForm;
            })
        );
    }

    public isPrefillCompleted$(): Observable<boolean> {
        return this.prefillService.state$.pipe(
            map(prefillState => prefillState[FormName.SubsidyEligibility].isPrefilled),
            filter(Boolean),
            first()
        );
    }
}
